if (!customElements.get("video-background")) {
  customElements.define(
    "video-background",
    class VideoBackground extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.intersecting = false;
        this.videoLoaded = false;
        this.ytPlayer = null;
        this.vimeoPlayer = null;
        this.nativeVideo = null;
        this._playPauseBound = false;
      }

      connectedCallback() {
        this.videoType = this.dataset.type;
        this.videoId = this.dataset.videoId;
        this.sectionId = this.dataset.sectionId;
        this.videoElement = this.querySelector(`#${this.sectionId}-video-player`);
        this.videoLoaderElement = this.querySelector(".video-background-loader");
        this.videoPlayerWrapper = this.querySelector(".video-background-player");
        const controlsId = this.dataset.controlsId;
        this.playPauseButton = controlsId
          ? document.getElementById(controlsId)
          : this.querySelector("[data-video-background-toggle]");

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.intersecting) {
                this.intersecting = true;
                this.initializeVideo();
                observer.unobserve(this);
              }
            });
          },
          {
            rootMargin: "200px",
          },
        );

        this.observer.observe(this);
      }

      getLabelPlay() {
        return this.dataset.labelPlay || "Play background video";
      }

      getLabelPause() {
        return this.dataset.labelPause || "Pause background video";
      }

      bindPlayPauseControl() {
        if (!this.playPauseButton || this._playPauseBound) {
          return;
        }
        this._playPauseBound = true;
        this.playPauseButton.addEventListener("click", () => this.togglePlayPause());
        this.playPauseButton.disabled = true;
      }

      updatePlayPauseUi(isPlaying) {
        if (!this.playPauseButton) {
          return;
        }
        const label = isPlaying ? this.getLabelPause() : this.getLabelPlay();
        const statusEl = this.playPauseButton.querySelector("[data-video-background-status]");
        if (statusEl) {
          statusEl.textContent = label;
          this.playPauseButton.removeAttribute("aria-label");
        } else {
          this.playPauseButton.setAttribute("aria-label", label);
        }
        this.playPauseButton.classList.toggle("is-paused", !isPlaying);
      }

      togglePlayPause() {
        switch (this.videoType) {
          case "self-hosted":
            if (!this.nativeVideo) {
              return;
            }
            if (this.nativeVideo.paused) {
              this.nativeVideo.play().catch(() => {});
            } else {
              this.nativeVideo.pause();
            }
            break;
          case "youtube":
            if (!this.ytPlayer || typeof YT === "undefined") {
              return;
            }
            if (this.ytPlayer.getPlayerState() === YT.PlayerState.PLAYING) {
              this.ytPlayer.pauseVideo();
            } else {
              this.ytPlayer.playVideo();
            }
            break;
          case "vimeo":
            if (!this.vimeoPlayer) {
              return;
            }
            this.vimeoPlayer.getPaused().then((paused) => {
              if (paused) {
                this.vimeoPlayer.play().catch(() => {});
              } else {
                this.vimeoPlayer.pause().catch(() => {});
              }
            });
            break;
          default:
            break;
        }
      }

      initializeVideo() {
        switch (this.videoType) {
          case "self-hosted":
            this.initializeSelfHostedVideo();
            break;
          case "youtube":
            VideoBackground.loadYouTube();
            this.initializeYouTube();
            break;
          case "vimeo":
            VideoBackground.loadVimeo();
            this.initializeVimeo();
            break;
        }
      }

      initializeSelfHostedVideo() {
        const player = this.querySelector("video");

        if (!player) {
          return;
        }

        this.nativeVideo = player;
        this.bindPlayPauseControl();

        if (!this.videoLoaded) {
          this.loadVideoSources(player);
        }

        const isVideoPlaying = !!(
          player.currentTime > 0 &&
          !player.paused &&
          !player.ended &&
          player.readyState > 2
        );

        if (isVideoPlaying) {
          this.showPlayer();
        }

        player.addEventListener("loadstart", () => {
          this.videoLoaderElement.classList.remove("is-hidden");
        });

        player.addEventListener("canplay", () => {
          if (this.playPauseButton) {
            this.playPauseButton.disabled = false;
          }
          if (!isVideoPlaying) {
            player.play().catch((error) => {
              console.warn("Video autoplay failed:", error);
            });
          }
          this.updatePlayPauseUi(!player.paused);
        });

        player.addEventListener("playing", () => {
          this.showPlayer();
          this.updatePlayPauseUi(true);
        });

        player.addEventListener("play", () => this.updatePlayPauseUi(true));
        player.addEventListener("pause", () => this.updatePlayPauseUi(false));

        player.addEventListener("error", () => {
          console.error("Video failed to load");
          this.videoLoaderElement.classList.add("is-hidden");
          this.setAttribute("data-loaded", "error");
        });
      }

      loadVideoSources(player) {
        const sources = player.querySelectorAll("source[data-src]");

        sources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute("data-src");
        });

        player.load();
        this.videoLoaded = true;
      }

      initializeYouTube() {
        if (typeof YT === "undefined" || typeof YT.Player === "undefined") {
          return setTimeout(() => {
            this.initializeYouTube();
          }, 333);
        }

        this.bindPlayPauseControl();

        this.ytPlayer = new YT.Player(`${this.sectionId}-video-player`, {
          videoId: this.videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            showinfo: 0,
            modestbranding: 0,
            loop: 1,
            playlist: this.videoId,
            fs: 0,
            autohide: 0,
            mute: 1,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              event.target.mute();
              event.target.playVideo();

              setTimeout(() => {
                this.showPlayer();
                this.updatePlayPauseUi(true);
              }, 1000);
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.showPlayer();
                this.updatePlayPauseUi(true);
              } else if (
                event.data === YT.PlayerState.PAUSED ||
                event.data === YT.PlayerState.CUED
              ) {
                this.updatePlayPauseUi(false);
              }
            },
          },
        });
      }

      initializeVimeo() {
        if (typeof Vimeo === "undefined" || typeof Vimeo.Player === "undefined") {
          return setTimeout(() => {
            this.initializeVimeo();
          }, 333);
        }

        this.bindPlayPauseControl();

        const player = new Vimeo.Player(this.videoElement, {
          id: this.videoId,
          loop: true,
          autoplay: true,
          byline: false,
          title: false,
          autopause: false,
          muted: true,
          playsinline: true,
          controls: false,
          background: true,
        });

        this.vimeoPlayer = player;

        player.setVolume(0);
        player.on("play", () => {
          this.showPlayer();
          this.updatePlayPauseUi(true);
        });
        player.on("pause", () => {
          this.updatePlayPauseUi(false);
        });
      }

      showPlayer() {
        this.videoPlayerWrapper.classList.add("visible");
        this.videoLoaderElement.classList.add("is-hidden");
        this.setAttribute("data-loaded", "true");
        if (this.playPauseButton) {
          this.playPauseButton.disabled = false;
        }
      }

      static loadYouTube() {
        if (window.TFP_YOUTUBE_LOADED) {
          return;
        }

        window.TFP_YOUTUBE_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://www.youtube.com/player_api";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      static loadVimeo() {
        if (window.TFP_VIMEO_LOADED) {
          return;
        }

        window.TFP_VIMEO_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://player.vimeo.com/api/player.js";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    },
  );
}
